import { CompanyMessage, TempUserMessage } from 'models';
import { guid } from 'globals/utils';
import capitalize from 'lodash/capitalize';
import intl from 'i18n';

import lockReasons from 'globals/lockReasons';

import * as chatAppActions from '../../app/ChatApp/actions';
import * as chatAppSelectors from '../../app/ChatApp/selectors';

import * as chatInputActions from '../ChatConversationInput/actions';
import * as helpers from './helpers';

import messages from './messages';

const COMPANY_MESSAGE_DELAY = 1500;

export const transformWaitTimeSeconds = seconds => {
  switch (true) {
    case seconds > 86400: {
      return `${Math.ceil(seconds / 86400)} ${intl.formatMessage(messages.days)}`;
    }
    case seconds > 3600: {
      return `${Math.ceil(seconds / 3600)} ${intl.formatMessage(messages.hours)}`;
    }
    case seconds > 60: {
      return `${Math.ceil(seconds / 60)} ${intl.formatMessage(messages.minutes)}`;
    }
    default: {
      return intl.formatMessage(messages.seconds);
    }
  }
};

export const formatMessagesForPost = (orderedSet, sessionId) => {
  const list = orderedSet.toList();
  const vcMessage = list.get(0);
  const channelSelection = list.get(1);
  const propertyQuestion = list.get(2);
  const propertyResponse = list.get(3);
  const thankYou = list.get(4);

  const property = channelSelection.body === 'Email' ? 'email' : 'phone';
  const capitalizedChannel = capitalize(channelSelection.body);

  return {
    session: sessionId,
    messages: [
      {
        prompt: vcMessage.body,
        promptAt: vcMessage.promptAt,
        input: capitalizedChannel,
        inputAt: channelSelection.inputAt,
        property: 'conversation_replyChannel',
      },
      {
        prompt: propertyQuestion.body,
        promptAt: propertyQuestion.promptAt,
        input: propertyResponse.body,
        inputAt: propertyResponse.inputAt,
        property: `customer_${property}`,
      },
      {
        prompt: thankYou.body,
        promptAt: thankYou.promptAt,
      },
    ],
  };
};

export const handleTimeout = (sessionId, settings) => (dispatch, getState) => {
  const isStillInVolumeControl = chatAppSelectors.getSessionIsInVolumeControl(getState(), sessionId);
  if (!isStillInVolumeControl) return;

  if (settings.markDoneAfterTimeout) {
    const lockReason = lockReasons.TIMED_OUT;
    dispatch(chatAppActions.markConversationDone(sessionId, lockReason));
    dispatch(chatAppActions.chatVolumeControlStop(sessionId));
  }
};

export const handleMaybeScheduleTimeout = (sessionId, settings) => dispatch => {
  if (!settings) return;

  const timeout = settings.timeout * 1000;

  if (timeout) {
    setTimeout(() => dispatch(handleTimeout(sessionId, settings)), timeout);
  }
};

export const handleSendFirstMessage = (sessionId, settings, upfront) => (dispatch, getState) => {
  const waitTimeSeconds = chatAppSelectors.selectSessionWaitTime(getState(), sessionId);
  const waitTime = transformWaitTimeSeconds(waitTimeSeconds);

  const messageOne = CompanyMessage({
    sessionId,
    body: upfront ? intl.formatMessage(messages.no_avail_upfront, { waitTime }) : intl.formatMessage(messages.no_avail),
    id: '__team',
  });
  dispatch(chatInputActions.messageSendOptimistically(messageOne));
  dispatch(chatAppActions.chatVolumeControlStart(sessionId));
  dispatch(handleMaybeScheduleTimeout(sessionId, settings));
};

export const handleMaybeSendFirstMessage = (sessionId, settings, upfront) => (dispatch, getState) => {
  const hasAgentResponded = chatAppSelectors.hasAgentInActiveSessionMessages(getState());
  const sessionLocked = chatAppSelectors.isSessionLocked(getState(), sessionId) || false;
  const isSessionInVolumeControl = chatAppSelectors.getSessionIsInVolumeControl(getState(), sessionId);

  if (!hasAgentResponded && !sessionLocked && !isSessionInVolumeControl) {
    dispatch(handleSendFirstMessage(sessionId, settings, upfront));
  }
};

export const handleMaybeStart = (sendToSessionId, triggerUpfront) => {
  return (dispatch, getState) => {
    const state = getState();
    const company = chatAppSelectors.getCompany(state);
    const isAfterHours = chatAppSelectors.selectIsAfterHours(state);
    const settings = (company && company.volumeControl) || {};
    const delay = settings && settings.promptDelay * 1000;
    const upfrontEnabled = chatAppSelectors.getUpfrontVolumeControlEnabled(state);

    if (upfrontEnabled && triggerUpfront && !isAfterHours && settings && settings.enabled) {
      dispatch(handleMaybeSendFirstMessage(sendToSessionId, settings, upfrontEnabled));
      return;
    }

    if (!upfrontEnabled && !triggerUpfront && !isAfterHours && settings && delay && settings.enabled) {
      helpers.createVolumeControlTimer({
        action: handleMaybeSendFirstMessage,
        dispatch,
        sendToSessionId,
        settings,
        upfrontEnabled,
        delay,
      });
    }
  };
};

export const handleChooseReplyChannel = (sessionId, response) => dispatch => {
  const channelName = response !== 'email' ? intl.formatMessage(messages.phone_number) : 'email';
  const body = intl.formatMessage(messages.channel_message, { channelName });
  const id = guid();
  const capitalizedChannel = capitalize(response);
  const message = TempUserMessage({ sessionId, body: capitalizedChannel, id });
  dispatch(chatInputActions.messageSendOptimistically(message));

  setTimeout(() => {
    const addressQuestion = CompanyMessage({ sessionId, body, id: '__team_1' });
    dispatch(chatInputActions.messageSendOptimistically(addressQuestion));
  }, COMPANY_MESSAGE_DELAY);
};

export const handleChooseWait = sessionId => dispatch => {
  dispatch(chatAppActions.chatVolumeControlStop(sessionId));
};

export const handleFormSubmit = sessionId => (dispatch, getState) => {
  const formMessages = chatAppSelectors.getActiveSessionMessages(getState());
  const volumeControlMessages = formMessages.slice(formMessages.size - 5);
  const data = formatMessagesForPost(volumeControlMessages, sessionId);

  dispatch(chatAppActions.submitVolumeControlForm(data));
};

export const handleSubmitReplyChannelAddress = (sessionId, response) => dispatch => {
  const id = guid();
  const addressMessage = TempUserMessage({ sessionId, body: response, id });
  const thankYou = intl.formatMessage(messages.thankYou_message);

  dispatch(chatInputActions.messageSendOptimistically(addressMessage));

  setTimeout(() => {
    const thankYouMessage = CompanyMessage({ sessionId, body: thankYou, id: '__team_2' });
    dispatch(chatInputActions.messageSendOptimistically(thankYouMessage));
    dispatch(handleFormSubmit(sessionId));
  }, COMPANY_MESSAGE_DELAY);
};
